import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Radio, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";

const when = (value) => value ? new Date(value).toLocaleString() : "Never";

export default function AdminPosSync() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async () => {
    try { setData((await api.get("/admin/pos/sync")).data); setError(""); }
    catch { setError("Could not load POS synchronization status."); }
  };
  useEffect(() => { load(); }, []);
  const sync = async () => {
    setBusy(true); setError("");
    try {
      const result = (await api.post("/admin/pos/sync", null, { timeout: 200000 })).data;
      setData(result);
      if (result.state.status === "succeeded") toast.success(`${result.published} POS listings published`);
      else if (result.state.status === "running") toast.info("A synchronization is already running");
      else setError(result.state.last_error || "POS synchronization failed.");
    } catch { setError("The sync request could not complete. Check the latest status."); }
    finally { setBusy(false); }
  };
  const state = data?.state || {};
  return (
    <section className="max-w-5xl min-w-0" data-testid="admin-pos-sync-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display font-black text-4xl" data-testid="pos-sync-heading">POS catalog</h1>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-none" onClick={load} disabled={busy} data-testid="pos-sync-refresh">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh status
          </Button>
          <Button className="rounded-none" onClick={sync} disabled={busy} data-testid="pos-sync-run">
            <Radio className={`w-4 h-4 mr-2 ${busy ? "animate-pulse" : ""}`} /> {busy ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      </div>
      {error && <p role="alert" className="text-red-700 border-l-4 border-red-600 pl-4 mb-6 break-words" data-testid="pos-sync-error">{error}</p>}
      {!data ? <p data-testid="pos-sync-loading">{error ? "Status unavailable" : "Loading status…"}</p> : <>
        <div className="grid sm:grid-cols-3 gap-6 border-y border-neutral-200 py-6 mb-8">
          {[['stored', 'Stored listings', data.stored], ['published', 'Published', data.published], ['status', 'Last result', state.status || 'Not started']].map(([key, label, value]) => (
            <div key={key}><div className="text-xs uppercase text-neutral-500 mb-2">{label}</div>
              <div className="font-display font-bold text-2xl break-words" data-testid={`pos-sync-${key}`}>{value}</div></div>
          ))}
        </div>
        <dl className="grid sm:grid-cols-[160px_minmax(0,1fr)] gap-x-6 gap-y-3 text-sm mb-8">
          <dt className="text-neutral-500">Feed URL</dt><dd className="break-all" data-testid="pos-sync-feed-url">{data.feed_url || "External POS is not configured"}</dd>
          <dt className="text-neutral-500">Configuration source</dt><dd data-testid="pos-sync-config-source">{data.configuration_source}</dd>
          <dt className="text-neutral-500">Last attempt</dt><dd data-testid="pos-sync-last-attempt">{when(state.last_attempt_at)}</dd>
          <dt className="text-neutral-500">Last successful sync</dt><dd data-testid="pos-sync-last-success">{when(state.last_success_at)}</dd>
          <dt className="text-neutral-500">Feed response</dt><dd data-testid="pos-sync-http-status">{state.http_status ? `HTTP ${state.http_status}` : "No response recorded"}</dd>
          <dt className="text-neutral-500">Received / pages</dt><dd data-testid="pos-sync-counts">{state.items_received ?? "—"} / {state.pages ?? "—"}</dd>
          <dt className="text-neutral-500">Refresh cadence</dt><dd data-testid="pos-sync-cadence">{data.refresh_seconds ? `${data.refresh_seconds}s on marketplace activity` : "POS_POLL_SECONDS is missing"} · {data.scheduled_minutes}m scheduled</dd>
          <dt className="text-neutral-500">Scheduler secret</dt><dd data-testid="pos-sync-scheduler">{data.scheduler_configured ? "Configured" : "WEBHOOK_CRON_SECRET is missing"}</dd>
          <dt className="text-neutral-500">Version</dt><dd data-testid="pos-sync-version">{data.version}</dd>
        </dl>
        {state.last_error && <p role="alert" className="mb-6 bg-red-50 text-red-800 p-4 break-words" data-testid="pos-sync-last-error">{state.last_error}</p>}
        {data.configuration_error && <p role="alert" className="mb-6 bg-red-50 text-red-800 p-4 break-words" data-testid="pos-sync-config-error">{data.configuration_error}</p>}
        {data.feed_url?.includes(".preview.") && <p className="mb-6 border-l-4 border-amber-500 pl-4 text-sm" data-testid="pos-sync-preview-warning">The POS source is a preview address; availability depends on that POS environment.</p>}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display font-bold text-lg">Stored POS listings</h2>
          <Link to="/products" className="text-sm underline inline-flex items-center gap-2" data-testid="pos-sync-marketplace-link">Marketplace <ExternalLink className="w-4 h-4" /></Link>
        </div>
        <div className="divide-y divide-neutral-200">
          {data.listings.map((item) => <div key={item.listing_id} className="py-4 flex flex-wrap justify-between gap-3" data-testid={`pos-sync-listing-${item.listing_id}`}>
            <div className="min-w-0 flex-1 basis-48">
              {item.status === "published" ? <Link className="font-semibold underline break-all" to={`/products/${item.product_id}`} data-testid={`pos-sync-product-${item.listing_id}`}>{item.title}</Link> : <span className="font-semibold break-all" data-testid={`pos-sync-title-${item.listing_id}`}>{item.title}</span>}
              <div className="text-xs text-neutral-500 mt-1 break-all" data-testid={`pos-sync-id-${item.listing_id}`}>{item.listing_id}</div>
            </div>
            <div className="text-sm" data-testid={`pos-sync-stock-${item.listing_id}`}>{item.status} · {item.stock} in stock</div>
          </div>)}
          {!data.listings.length && <p className="py-6 text-neutral-500" data-testid="pos-sync-empty">No POS listings stored.</p>}
        </div>
      </>}
    </section>
  );
}